/**
 * This provides a consistent interface for config updates across all layouts,
 * allowing components to be layout-agnostic. Each layout provides its own
 * implementation of this context.
 */
import {type MediaCardType} from '@wandb/weave/common/types/media';
import React, {useContext} from 'react';

import {type RunWithHistoryAndMediaWandb} from '../../../MediaCard';
import {
  type MediaBrowserPanelProps,
  type MediaConfigWithDefaults,
} from '../../Component';
import {type StepMeta} from '../../types';
import {
  type ControlData,
  type GridCell,
  type GridData,
  type RowData,
} from '../types';

/**
 * All layouts implement this interface, but with different behaviors:
 * - Compare mode: Uses columnIndex for column-specific updates
 * - Gallery/Grid modes: Ignores columnIndex, uses shared updates
 */
export type LayoutConfigContextValue = {
  // Step dimension
  getStep: (columnIndex?: number, rowIndex?: number) => number;
  setStep: (value: number, columnIndex?: number) => void;
  getAvailableSteps: (columnIndex?: number) => number[] | undefined;

  // Index dimension
  getIndex: (columnIndex?: number, rowIndex?: number) => number;
  setIndex: (value: number, columnIndex?: number) => void;
  getAvailableIndices: (columnIndex?: number) => number[] | undefined;

  // Run dimension
  getRun: (columnIndex?: number, rowIndex?: number) => string | undefined;
  setRun: (runName: string, columnIndex?: number) => void;
  getAvailableRuns: (columnIndex?: number) => RunWithHistoryAndMediaWandb[];

  // MediaKey dimension
  // Column-specific (single key, requires columnIndex)
  getMediaKey: (columnIndex: number) => string | undefined;
  setMediaKey: (mediaKey: string, columnIndex: number) => void;
  // Top-level (multiple keys, no columnIndex)
  getMediaKeys: () => string[];
  setMediaKeys: (mediaKeys: string[]) => void;
  // Available options
  getAvailableMediaKeys: (columnIndex?: number) => string[];

  /**
   * Column width for all columns (same for all columns in a grid).
   * Used for constraining content width within columns.
   */
  columnWidth: number;
  /**
   * Media type for the panel.
   * Used to filter available media keys.
   */
  mediaType: MediaCardType;
  getCell: (columnIndex: number, rowIndex: number) => GridCell | undefined;
  /** True in the panel editor's preview, which renders next to the live panel. */
  isPreview: boolean;
};

export const LayoutConfigContext =
  React.createContext<LayoutConfigContextValue | null>(null);

/**
 * Hook to access the layout config context.
 *
 * Returns null if context is not available (for backward compatibility).
 */
export const useLayoutConfig = (): LayoutConfigContextValue | null => {
  const context = useContext(LayoutConfigContext);
  if (!context) {
    return null;
  }
  return context;
};

export type LayoutConfigProviderProps = {
  mediaType: MediaCardType;
  config: MediaConfigWithDefaults;
  availableRuns: RunWithHistoryAndMediaWandb[];
  availableMediaKeys: string[];
  stepMeta: StepMeta;
  gridData?: GridData; // Optional grid data for grid mode column value lookup
  controlData?: ControlData[]; // Optional precomputed per-column control data
  rowData?: RowData[]; // Optional precomputed per-row data
  columnWidth: number;
  configMode?: boolean;
  updateConfig: MediaBrowserPanelProps['updateConfig'];
  children: React.ReactNode;
};
